/**
 * What comes back up, as numbers.
 *
 * Same trick as `Blood.ts`, and written to match it deliberately: nine gouts
 * are thrown once, at load, and each one is a pure function of a single number
 * - how long ago Kimmo ate the parsakaalia. Ask where gout `i` is 0.4 s in and
 * you get the same answer on every machine, at every frame rate, however long
 * the tab spent in the background.
 *
 * That closed form is here for the reason it is there, only more so: a run is
 * restarted constantly, and a player who is bad at the minigame can be sick
 * three times in one attempt. Anything that accumulated would have to be torn
 * down on every wrong key as well as on every `R`. A closed form cannot leak,
 * and resetting it is passing `null`.
 *
 * The life is `AAMUPALA.vomitDuration` rather than a number of its own,
 * because what this is *for* is showing how much of the pause is left. See the
 * `OKSENNUS` block in src/config/gameplay.ts.
 *
 * No DOM in here, and no three.js: `AamupalaOverlay` owns the canvas, this owns
 * what goes on it. Same split as Blood, Plume and Shatter, and for the same
 * reason - it can be sampled ten thousand times in a test with no canvas
 * anywhere near it.
 */

import { AAMUPALA } from '../config/aamupala';
import { OKSENNUS } from '../config/gameplay';
import { lerp } from '../utils/math';

/** Seconds the mess is up: exactly as long as the pan is stopped. */
export const VOMIT_DURATION = AAMUPALA.vomitDuration;

/** One speck thrown off a gout, in the gout's own units. */
export interface FleckSeed {
  angle: number;
  distance: number;
  radius: number;
}

/** One hump of a gout's outline: how far off round, and where. */
export interface LobeSeed {
  amplitude: number;
  phase: number;
}

/** One gout's fixed identity. Authored once, at load, never stepped. */
export interface GoutSeed {
  /** Where it left from. `x` is a fraction of the width, `y` of the height. */
  fromX: number;
  fromY: number;
  /** How far up the frame it climbs, and how far sideways, in height fractions. */
  riseTo: number;
  drift: number;
  /** Radius as a fraction of the screen's height. */
  radius: number;
  /** How solid it is at its fullest. */
  alpha: number;
  /** Seconds after the mouthful that this one leaves. */
  delay: number;
  /** Which way it is pointing, radians. Its tail points back down it. */
  heading: number;
  /** How much longer it is along that heading than across it. */
  stretch: number;
  /** The spike off its trailing end, in radii. */
  tail: number;
  /** How far back down it runs over the rest of the life, in height fractions. */
  run: number;
  lobes: LobeSeed[];
  specks: FleckSeed[];
}

/** Where a gout is this frame, and how much of it is left. */
export interface GoutPose {
  x: number;
  y: number;
  /** Height fractions, as authored - a gout does not grow. */
  radius: number;
  /** 0..1, and 0 both before it leaves and after the pause is over. */
  opacity: number;
  /** How far it has run back down, in height fractions. Also its trail. */
  drip: number;
}

export function goutPose(): GoutPose {
  return { x: 0, y: 0, radius: 0, opacity: 0, drip: 0 };
}

/**
 * The gouts, thrown up out of the bottom of the frame.
 *
 * `rng` is expected to be a seeded generator called at load time and never
 * again - the same allowance src/utils/prng.ts is written for. Nothing about
 * this is random once the page has loaded, so two people watching one replay
 * watch the same man be sick in the same places.
 *
 * Placed along the mouth rather than round a circle, because unlike the blood
 * this all came out of one hole. What separates the gouts is how far each one
 * got, which is what makes three heaves read as three heaves.
 */
export function goutSeeds(rng: () => number): GoutSeed[] {
  const seeds: GoutSeed[] = [];
  for (let i = 0; i < OKSENNUS.count; i++) {
    const fromX = OKSENNUS.mouth.x + (rng() - 0.5) * 2 * OKSENNUS.mouth.spread;
    const radius = lerp(OKSENNUS.radius.min, OKSENNUS.radius.max, Math.pow(rng(), OKSENNUS.sizeBias));
    // How much of the size range this one has, 0 at a fleck and 1 at the
    // heaviest. The big ones are thrown least far and run furthest, which is
    // what mass does and is most of why this reads as weight rather than as
    // confetti.
    const heft = (radius - OKSENNUS.radius.min) / (OKSENNUS.radius.max - OKSENNUS.radius.min);
    const drift = (rng() - 0.5) * 2 * OKSENNUS.sideways;
    const riseTo = OKSENNUS.reach * lerp(1, 0.35, heft) * lerp(0.55, 1, rng());
    // Pointing the way it went: up the frame, leaning the way it drifted.
    const heading = -Math.PI / 2 + Math.atan2(drift, Math.max(riseTo, 1e-3));

    const lobes: LobeSeed[] = [];
    for (let lobe = 0; lobe < OKSENNUS.lobes; lobe++) {
      lobes.push({
        amplitude: (OKSENNUS.wobble * lerp(0.4, 1, rng())) / (lobe + 1),
        phase: rng() * Math.PI * 2,
      });
    }

    const specks: FleckSeed[] = [];
    const thrown = Math.floor(rng() * (OKSENNUS.satellites + 1));
    for (let speck = 0; speck < thrown; speck++) {
      specks.push({
        angle: heading + (rng() - 0.5) * OKSENNUS.satelliteSpread,
        distance: lerp(1.3, OKSENNUS.satelliteReach, rng()),
        radius: lerp(OKSENNUS.satelliteRadius.min, OKSENNUS.satelliteRadius.max, rng()),
      });
    }

    seeds.push({
      fromX,
      fromY: OKSENNUS.mouth.y,
      riseTo,
      drift,
      radius,
      alpha: lerp(OKSENNUS.alpha.min, OKSENNUS.alpha.max, rng()),
      delay: (i / OKSENNUS.count) * OKSENNUS.stagger * lerp(0.7, 1, rng()),
      heading,
      stretch: OKSENNUS.stretch * lerp(0.4, 1, rng()),
      tail: OKSENNUS.tail * lerp(0.2, 1, rng()),
      run: OKSENNUS.run * lerp(0.4, 1, heft) * lerp(0.6, 1, rng()),
      lobes,
      specks,
    });
  }
  return seeds;
}

/**
 * Gout `seed`, `age` seconds after the mouthful, into `out`.
 *
 * Written into a caller-owned pose rather than returned, for the reason
 * `sampleSplat` is: this runs once per gout per frame, and nine allocations a
 * frame is a joke that shows up in a profile.
 *
 * Everything ends at `VOMIT_DURATION` regardless of when it left, so the
 * screen clears on one frame instead of dribbling out gout by gout after the
 * pan has already started up again.
 */
export function sampleGout(seed: GoutSeed, age: number, out: GoutPose): GoutPose {
  out.x = seed.fromX;
  out.y = seed.fromY;
  out.radius = seed.radius;
  out.opacity = 0;
  out.drip = 0;

  const since = age - seed.delay;
  if (since <= 0 || age >= VOMIT_DURATION) return out;

  // Up first, then back down. The climb is one `rise`-long ease-out, which is
  // the arc of something thrown; everything after it is the slide, and the
  // slide is what the rest of the pause is spent watching.
  const climb = Math.min(1, since / OKSENNUS.rise);
  const eased = 1 - (1 - climb) * (1 - climb);
  const slid = Math.max(0, since - OKSENNUS.rise);
  const run = seed.run * Math.pow(slid / Math.max(1e-3, VOMIT_DURATION - OKSENNUS.rise), OKSENNUS.runPower);

  out.x = seed.fromX + seed.drift * eased;
  out.y = seed.fromY - seed.riseTo * eased + run;
  out.drip = run;

  const clearing = OKSENNUS.hold * VOMIT_DURATION;
  const fade = age <= clearing ? 1 : (VOMIT_DURATION - age) / (VOMIT_DURATION - clearing);
  out.opacity = seed.alpha * climb * fade;
  return out;
}

/**
 * The green over the whole frame, 0..1, `age` seconds in.
 *
 * Up fast and down slowly, because that is the order those two things happen
 * in: the wash is the moment it comes up, and the rest of it is Kimmo getting
 * over it. Zero outside the life, so the frame is clean the moment the pan
 * starts again.
 */
export function nausea(age: number): number {
  if (age <= 0 || age >= VOMIT_DURATION) return 0;
  const u = age / VOMIT_DURATION;
  return u < 0.18 ? u / 0.18 : (1 - u) / 0.82;
}
